define([
    'backbone',
    'alert',
    'appcontext'
], (Backbone, Alert, appContext) => {

    const urlError = function () {
        throw new Error('A "url" property or function must be specified');
    }; const setOptions = {add: true, remove: true, merge: true};

    /*
     * Backbone 1.3.3 source code
     * Splices `insert` into `array` at index `at`.
     */
    const splice = function (array, insert, at) {
        at = Math.min(Math.max(at, 0), array.length);
        const tail = Array(array.length - at);
        const length = insert.length;
        let i;
        for (i = 0; i < tail.length; i++) {
            tail[i] = array[i + at];
        }
        for (i = 0; i < length; i++) {
            array[i + at] = insert[i];
        }
        for (i = 0; i < tail.length; i++) {
            array[i + length + at] = tail[i];
        }
    };

    return Backbone.Collection.extend({
        apiPrefix: '/api',
        namespace: null,
        totalModel: false,

        defaults: {
            firstPage: 1,
            currentPage: 1,
            perPage () {
                return appContext.getItemsOnPage();
            },
            itemsTotal: 0,
            pagesTotal: 1,
            lastPage: 1,
            pageArgName: 'page',
            perPageArgName: 'per_page',
            itemsKeyName: 'items',
            itemsTotalKeyName: 'items_total',
            updateCurrentPage: null,
            isFetching: false
        },

        buildUrl (url, queryParams) {
            let base = _.result(this, 'apiPrefix');
            if (this.namespace) {
                base += '/{0}'.format(_.result(this, 'namespace'));
            }
            let prefixedUrl = base + (url || urlError());
            if (_.isObject(queryParams)) {
                prefixedUrl = '{0}?{1}'.format(prefixedUrl, $.param(queryParams));
            }
            return prefixedUrl;
        },

        sync (method, model, options) {
            if (!options.url) {
                options.url = this.buildUrl(_.result(model, 'url'), options.queryParams);
            }
            return Backbone.Collection.prototype.sync.apply(this, arguments);
        },

        initialize (models, options) {
            _.extend(this, this.defaults, options || {});
            this.filters || (this.filters = {});
            this.sorting || (this.sorting = []);
            this._fields || (this._fields = []);
            this.pageSelected || (this.pageSelected = false);
            this.allSelected || (this.allSelected = false);
            this.allSelected ? this.pageSelected = this.allSelected : {};

            if (this.totalModel) {
                const TotalModel = this.totalModel === true ? Backbone.Model : this.totalModel;
                this.total = new TotalModel();
            }

            if (!_.isArray(this.sorting)) {
                this.sorting = this.migrateSorting(this.sorting);
            }
        },

        clone () {
            const collection = Backbone.Collection.prototype.clone.call(this);
            collection.filters = this.filters;
            collection.sorting = this.sorting;
            collection.updateCurrentPage = this.updateCurrentPage;
            return collection;
        },

        parse (response) {
            if (this.itemsTotalKeyName in response) {
                this.itemsTotal = response[this.itemsTotalKeyName];
                this.pagesTotal = Math.ceil(this.itemsTotal / this.perPage());
                this.lastPage = this.pagesTotal;
            }
            let items = this.itemsKeyName in response ? response[this.itemsKeyName] : response;
            if (!_.isArray(items)) {
                items = [];
                Alert.clear().error({
                    title: appContext.tr('Error'),
                    text: appContext.tr('Server returned unexpected data')
                });
            }
            if (this.total) {
                this.total.set(this.total.parse(response.total));
            }
            return items;
        },

        reset () {
            const rv = Backbone.Collection.prototype.reset.apply(this, arguments);
            if (this.pageSelected) {
                this.each((model) => {
                    model.trigger('selected', true);
                    model.selected = true;
                });
            }
            return rv;
        },

        fetch (options) {
            options || (options = {});
            options.data = _.extend({}, options.data, this.filters, this.serializeSorting());
            return Backbone.Collection.prototype.fetch.call(this, options);
        },

        fetchPage (page, options) {
            if (!this.isFetching) {
                this.isFetching = true;
                options || (options = {});
                options.data || (options.data = {});
                options.data[this.pageArgName] = page;
                options.data[this.perPageArgName] = this.perPage();

                const self = this;
                const prevCurrentPage = this.currentPage;
                self.currentPage = page;
                return this.fetch(options).fail(() => {
                    self.currentPage = prevCurrentPage;
                }).
                    always(() => {
                        self.isFetching = false;
                    });
            }
            const response = new $.Deferred();
            response.reject();
            return response.promise();
        },

        fetchCurrentPage (options) {
            return this.fetchPage(this.currentPage, options);
        },

        fetchNextPage (options) {
            return this.fetchPage(this.currentPage + 1, options);
        },

        fetchPrevPage (options) {
            return this.fetchPage(this.currentPage - 1, options);
        },

        fetchFirstPage (options) {
            return this.fetchPage(this.firstPage, options);
        },

        fetchLastPage (options) {
            return this.fetchPage(this.pagesTotal, options);
        },

        hasNextPage () {
            return this.currentPage < this.lastPage;
        },

        hasPrevPage () {
            return this.currentPage > this.firstPage;
        },

        isPageCurrent (page) {
            return page === this.currentPage;
        },

        filtering (name, value) {
            if (_.isNull(value) || `${value}`.length === 0) {
                delete this.filters[name];
                return this;
            } else if (_.isUndefined(value)) {
                if (_.isObject(name)) {
                    _.each(name, function (value, key) {
                        this.filtering(key, value);
                    }, this);
                    return this;
                }
                return this.filters[name];

            }
            this.filters[name] = value;
            return this;

        },

        sort (sorting) {
            if (!_.isArray(sorting)) {
                sorting = this.migrateSorting(sorting);
            }
            this.sorting = sorting;
            return this;
        },

        fields (fields) {
            if (_.isUndefined(fields)) {
                return this._fields;
            }
            this._fields = fields;
            return this;

        },

        migrateSorting (sorting) {
            return _.map(sorting, (direction, name) => ({name, direction: direction === 'asc' ? 1 : -1}));
        },

        serializeSorting () {
            const results = _.map(this.sorting, (item) => (item.direction < 0 ? '-' : '') + item.name);
            return results.length ? {sorting: results.join(' ')} : {};
        },

        parseSorting (str) {
            if (!str) {
                return;
            }

            const sorting = str.split(' ').map((field) => {
                const desc = field[0] === '-';
                return {name: desc ? field.slice(1) : field, direction: desc ? -1 : 1};
            });

            if (sorting.length) {
                this.sort(sorting);
            }
        },

        parseParams (params) {
            function tryParse (val) {
                return _.isFinite(val) ? Number(val) : val;
            }

            if (!_.isEmpty(params)) {
                this.filters = {};

                this.parseSorting(params.sorting);
                const filters = _.omit(params, 'sorting');

                // trying to parse ids as numbers
                _.each(filters, (value, key) => {
                    filters[key] = _.isArray(value) ? _.map(value, tryParse) : tryParse(value);
                });
                this.filtering(filters);
            }
        },

        hasSelected () {
            return !_.isUndefined(this.find((model) => model.selected === true));
        },

        selected () {
            const filtered = this.filter((model) => model.selected === true);
            return this.clone().reset(filtered);
        },

        applyToSelected (func) {
            return this.selected().map((model) => func(model));
        },

        filterBySelection (params, options) {
            options = _.defaults(options || {}, {paramsKey: 'id', modelKey: 'id'});
            if (!this.allSelected) {
                if (this.hasSelected()) {
                    params[options.paramsKey] = this.applyToSelected((m) => m.get(options.modelKey));
                } else if (!options.noSelectionExportAll) {
                    params[options.paramsKey] = [-1];
                }
            }
            return this;
        },

        selectionFilters (options) {
            const params = _.clone(this.filters);
            this.filterBySelection(params, options);
            return params;
        },

        totalSelectedCount () {
            const itemsTotal = this.itemsTotal;
            const perPage = this.perPage();
            if (this.allSelected) {
                return itemsTotal;
            } else if (this.pageSelected) {
                return itemsTotal > perPage ? perPage : itemsTotal;
            }
            return this.selected().length;

        },

        selectPage (selected) {
            selected = _.isUndefined(selected) ? true : selected;
            this.pageSelected = selected;
            !selected ? this.selectAll(false) : {};
            this.each((model) => {
                model.selected = selected;
                model.trigger('selected', selected);
            });

            this.allSelected = this.selected().length === this.itemsTotal;
            this.trigger('pageSelect', selected);
            return this;
        },

        selectAll (selected) {
            selected = _.isUndefined(selected) ? true : selected;
            this.allSelected = selected;
            selected !== this.pageSelected ? this.selectPage(selected) : {};
            this.trigger('allSelect', selected);
            return this;
        },

        /*
         * Method for checking whether an object should be considered a model for
         * the purposes of adding to the collection.
         */
        _isModel (model) {
            return model instanceof Backbone.Model;
        },

        // also copied (and refactored a little to comply our JS code style) from Backbone sources
        _removeModels (models, options) {
            const removed = [];
            for (let i = 0; i < models.length; i += 1) {
                const model = this.get(models[i]);
                if (model) {
                    const index = this.indexOf(model);
                    this.models.splice(index, 1);
                    this.length -= 1;

                    delete this._byId[model.cid];
                    const id = this.modelId(model.attributes);
                    if (id !== null) {
                        delete this._byId[id];
                    }

                    if (!options.silent) {
                        options.index = index;
                        model.trigger('remove', model, this, options);
                    }

                    removed.push(model);
                    this._removeReference(model, options);
                }
            }
            return removed;
        },

        // Backbone 1.3.3 source code
        /*
         * Update a collection by `set`-ing a new list of models, adding new ones,
         * removing models that are no longer present, and merging models that
         * already exist in the collection, as necessary. Similar to **Model#set**,
         * the core operation for updating the data contained by the collection.
         */
        set (models, options) {
            if (models === null) {
                return;
            }

            options = _.extend({}, setOptions, options);
            if (options.parse && !this._isModel(models)) {
                models = this.parse(models, options) || [];
            }

            const singular = !_.isArray(models);
            models = singular ? [models] : models.slice();

            let at = options.at;
            if (at !== null) {
                at = Number(at);
            }
            if (at > this.length) {
                at = this.length;
            }
            if (at < 0) {
                at += this.length + 1;
            }

            const set = [];
            const toAdd = [];
            const toMerge = [];
            const toRemove = [];
            const modelMap = {};

            const add = options.add;
            const merge = options.merge;
            const remove = options.remove;

            let sort = false;
            const sortable = this.comparator && at === null && options.sort !== false;
            const sortAttr = _.isString(this.comparator) ? this.comparator : null;

            /*
             * Turn bare objects into model references, and prevent invalid models
             * from being added.
             */
            let model; let i;
            for (i = 0; i < models.length; i++) {
                model = models[i];

                /*
                 * If a duplicate is found, prevent it from being added and
                 * optionally merge it into the existing model.
                 */
                const existing = this.get(model);
                if (existing) {
                    if (merge && model !== existing) {
                        let attrs = this._isModel(model) ? model.attributes : model;
                        if (options.parse) {
                            attrs = existing.parse(attrs, options);
                        }
                        existing.set(attrs, options);
                        toMerge.push(existing);
                        if (sortable && !sort) {
                            sort = existing.hasChanged(sortAttr);
                        }
                    }
                    if (!modelMap[existing.cid]) {
                        modelMap[existing.cid] = true;
                        set.push(existing);
                    }
                    models[i] = existing;

                    // If this is a new, valid model, push it to the `toAdd` list.
                } else if (add) {
                    model = models[i] = this._prepareModel(model, options);
                    if (model) {
                        toAdd.push(model);
                        this._addReference(model, options);
                        modelMap[model.cid] = true;
                        set.push(model);
                    }
                }
            }

            // Remove stale models.
            if (remove) {
                for (i = 0; i < this.length; i++) {
                    model = this.models[i];
                    if (!modelMap[model.cid]) {
                        toRemove.push(model);
                    }
                }
                if (toRemove.length) {
                    this._removeModels(toRemove, options);
                }
            }

            // See if sorting is needed, update `length` and splice in new models.
            let orderChanged = false;
            const replace = !sortable && add && remove;
            if (set.length && replace) {
                orderChanged = this.length !== set.length || _.some(this.models, (m, index) => m !== set[index]);
                this.models.length = 0;
                splice(this.models, set, 0);
                this.length = this.models.length;
            } else if (toAdd.length) {
                if (sortable) {
                    sort = true;
                }
                splice(this.models, toAdd, at === null ? this.length : at);
                this.length = this.models.length;
            }

            // Silently sort the collection if appropriate.
            if (sort) {
                this.sort({silent: true});
            }

            // Unless silenced, it's time to fire all appropriate add/sort/update events.
            if (!options.silent) {
                for (i = 0; i < toAdd.length; i++) {
                    if (at !== null) {
                        options.index = at + i;
                    }
                    model = toAdd[i];
                    model.trigger('add', model, this, options);
                }
                if (sort || orderChanged) {
                    this.trigger('sort', this, options);
                }
                if (toAdd.length || toRemove.length || toMerge.length) {
                    options.changes = {
                        added: toAdd,
                        removed: toRemove,
                        merged: toMerge
                    };
                    this.trigger('update', this, options);
                }
            }

            // Return the added (or merged) model (or models).
            return singular ? models[0] : models;
        },

        modelId (attrs) {
            return attrs[this.model.prototype.idAttribute || 'id'];
        },

        _addReference (model) {
            this._byId[model.cid] = model;
            const id = this.modelId(model.attributes);
            if (id !== null) {
                this._byId[id] = model;
            }
            model.on('all', this._onModelEvent, this);
        }
    });
});
